import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Shield, Plus, Trash2, Pencil, Search, Zap, Loader2, CheckCircle2, XCircle,
} from "lucide-react";
import {
  imageBlocklistApi,
  type ImageBlocklistEntry,
} from "@/api/image-blocklist";

// ── Category badge colors ──

const CATEGORY_COLORS: Record<string, string> = {
  tracking: "bg-red-100 text-red-800 border-red-200",
  platform_ui: "bg-blue-100 text-blue-800 border-blue-200",
  icon: "bg-gray-100 text-gray-800 border-gray-200",
  decoration: "bg-yellow-100 text-yellow-800 border-yellow-200",
  logo: "bg-purple-100 text-purple-800 border-purple-200",
  other: "bg-green-100 text-green-800 border-green-200",
};

const CATEGORY_LABELS: Record<string, string> = {
  tracking: "Tracking 追蹤",
  platform_ui: "Platform UI 平台元素",
  icon: "Icon 圖示",
  decoration: "Decoration 裝飾",
  logo: "Logo 標誌",
  other: "Other 其他",
};

const PATTERN_TYPES = ["domain", "path", "filename", "regex"] as const;
const CATEGORIES = ["tracking", "platform_ui", "icon", "decoration", "logo", "other"] as const;

// ── Empty form state ──

interface FormState {
  pattern: string;
  pattern_type: string;
  category: string;
  notes: string;
}

const EMPTY_FORM: FormState = { pattern: "", pattern_type: "domain", category: "tracking", notes: "" };

// ── Main component ──

export default function ImageBlocklist() {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<ImageBlocklistEntry | null>(null);
  const [testUrl, setTestUrl] = useState("");
  const [applyConfirm, setApplyConfirm] = useState(false);

  // ── Queries ──

  const { data: entriesRes, isLoading } = useQuery({
    queryKey: ["image-blocklist"],
    queryFn: () => imageBlocklistApi.list(),
  });
  const entries = (entriesRes?.data ?? []) as ImageBlocklistEntry[];

  // ── Mutations ──

  const createMut = useMutation({
    mutationFn: (data: FormState) => imageBlocklistApi.create(data),
    onSuccess: () => {
      toast.success("Pattern created");
      queryClient.invalidateQueries({ queryKey: ["image-blocklist"] });
      closeForm();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<FormState & { is_active: boolean }> }) =>
      imageBlocklistApi.update(id, data),
    onSuccess: () => {
      toast.success("Pattern updated");
      queryClient.invalidateQueries({ queryKey: ["image-blocklist"] });
      closeForm();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => imageBlocklistApi.delete(id),
    onSuccess: () => {
      toast.success("Pattern deleted");
      queryClient.invalidateQueries({ queryKey: ["image-blocklist"] });
      setDeleteTarget(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const testMut = useMutation({
    mutationFn: (url: string) => imageBlocklistApi.testUrl(url),
  });

  const applyMut = useMutation({
    mutationFn: () => imageBlocklistApi.applyAll(),
    onSuccess: (res) => {
      const d = res.data;
      toast.success(`Batch cleanup done: ${d?.blocked ?? 0} blocked, ${d?.cleaned ?? 0} cleaned`);
      setApplyConfirm(false);
    },
    onError: (e: Error) => {
      toast.error(e.message);
      setApplyConfirm(false);
    },
  });

  // ── Helpers ──

  function closeForm() {
    setFormOpen(false);
    setEditId(null);
    setForm(EMPTY_FORM);
  }

  function openEdit(entry: ImageBlocklistEntry) {
    setEditId(entry.id);
    setForm({
      pattern: entry.pattern,
      pattern_type: entry.pattern_type,
      category: entry.category,
      notes: entry.notes ?? "",
    });
    setFormOpen(true);
  }

  function handleSubmit() {
    if (!form.pattern.trim()) {
      toast.error("Pattern is required");
      return;
    }
    if (editId) {
      updateMut.mutate({ id: editId, data: form });
    } else {
      createMut.mutate(form);
    }
  }

  function toggleActive(entry: ImageBlocklistEntry) {
    updateMut.mutate({ id: entry.id, data: { is_active: !entry.is_active } });
  }

  // ── Stats ──

  const totalRules = entries.length;
  const activeRules = entries.filter(e => e.is_active).length;
  const topHits = [...entries].sort((a, b) => b.hit_count - a.hit_count).slice(0, 3);

  // ── Render ──

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Image Blocklist 圖片過濾</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="destructive" onClick={() => setApplyConfirm(true)}>
            <Zap className="h-4 w-4 mr-1" />
            Batch Cleanup 批次清理
          </Button>
          <Button onClick={() => { setForm(EMPTY_FORM); setEditId(null); setFormOpen(true); }}>
            <Plus className="h-4 w-4 mr-1" />
            Add Pattern 新增規則
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Rules 總規則</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{totalRules}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active 啟用中</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600">{activeRules}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Top Hits 命中最多</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {topHits.map((e, i) => (
                <div key={e.id} className="flex items-center justify-between text-sm">
                  <span className="truncate max-w-[180px]">{i + 1}. {e.pattern}</span>
                  <Badge variant="secondary">{e.hit_count}</Badge>
                </div>
              ))}
              {topHits.length === 0 && <span className="text-sm text-muted-foreground">No data</span>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* URL Tester */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">URL Tester 網址測試</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Paste image URL to test..."
              value={testUrl}
              onChange={e => setTestUrl(e.target.value)}
              className="flex-1"
            />
            <Button
              onClick={() => testUrl.trim() && testMut.mutate(testUrl.trim())}
              disabled={testMut.isPending || !testUrl.trim()}
            >
              {testMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              <span className="ml-1">Test</span>
            </Button>
          </div>
          {testMut.data && (
            <div className="mt-3 p-3 rounded-md border">
              {testMut.data.data?.blocked ? (
                <div className="flex items-center gap-2 text-red-600">
                  <XCircle className="h-5 w-5" />
                  <span>Blocked 已攔截 — matched: <code className="bg-red-50 px-1 rounded">{testMut.data.data.matched_pattern}</code></span>
                  <Badge className={CATEGORY_COLORS[testMut.data.data.category ?? "other"]}>
                    {testMut.data.data.category}
                  </Badge>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="h-5 w-5" />
                  <span>Allowed 允許通過</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No blocklist entries yet. Click "Add Pattern" to create one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Active</TableHead>
                  <TableHead>Pattern 規則</TableHead>
                  <TableHead className="w-[100px]">Type 類型</TableHead>
                  <TableHead className="w-[120px]">Category 分類</TableHead>
                  <TableHead className="w-[80px] text-right">Hits</TableHead>
                  <TableHead className="w-[200px]">Notes 備註</TableHead>
                  <TableHead className="w-[100px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(entry => (
                  <TableRow key={entry.id} className={!entry.is_active ? "opacity-50" : ""}>
                    <TableCell>
                      <Switch
                        checked={entry.is_active}
                        onCheckedChange={() => toggleActive(entry)}
                      />
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-1.5 py-0.5 rounded">{entry.pattern}</code>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{entry.pattern_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={CATEGORY_COLORS[entry.category] ?? CATEGORY_COLORS.other}>
                        {entry.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">{entry.hit_count}</TableCell>
                    <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">
                      {entry.notes || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(entry)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(entry)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={open => { if (!open) closeForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Pattern 編輯規則" : "New Pattern 新增規則"}</DialogTitle>
            <DialogDescription>
              {editId ? "Update the blocklist pattern details." : "Add a new image URL pattern to block."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Pattern 規則</Label>
              <Input
                placeholder="e.g. s.alicdn.com, tracking.gif, /icons/"
                value={form.pattern}
                onChange={e => setForm(f => ({ ...f, pattern: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Type 類型</Label>
                <Select value={form.pattern_type} onValueChange={v => setForm(f => ({ ...f, pattern_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PATTERN_TYPES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Category 分類</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes 備註</Label>
              <Textarea
                placeholder="Optional notes..."
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeForm}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={createMut.isPending || updateMut.isPending}
            >
              {(createMut.isPending || updateMut.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {editId ? "Save 儲存" : "Create 建立"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pattern 刪除規則</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <code className="bg-muted px-1 rounded">{deleteTarget?.pattern}</code>? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Delete 刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Apply-all confirm */}
      <AlertDialog open={applyConfirm} onOpenChange={setApplyConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Batch Cleanup 批次清理</AlertDialogTitle>
            <AlertDialogDescription>
              This will apply all active blocklist patterns to every stored image URL across all tasks. Blocked images will be removed from task data. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => applyMut.mutate()}
              className="bg-red-600 hover:bg-red-700"
            >
              {applyMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Confirm 確認清理
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
